import { computed, h } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import {
  At16,
  Book16,
  Bot16,
  Bot20,
  Book20,
  Building16,
  Category16,
  Chat16,
  Chat20,
  ChartBar16,
  ChartLine16,
  ChartLine20,
  ChatBot16,
  Connect16,
  Dashboard16,
  Document16,
  Email20,
  Flag16,
  Folder16,
  Group16,
  Notification16,
  Phone16,
  Play16,
  Rocket16,
  Settings16,
  SettingsAdjust16,
  Tag16,
  Time16,
  Timer16,
  Translate16,
  User16,
  User20,
  UserAvatar16,
  UserFollow16,
  UserMultiple16,
} from '@carbon/icons-vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useAssigneeTab } from 'dashboard/composables/useAssigneeTab';
import {
  filterItemsByPermission,
  getUserPermissions,
} from 'dashboard/helper/permissionsHelper.js';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';
import { SIDEBAR_SORT_SECTIONS } from 'dashboard/helper/sidebarSort';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import { useSidebarData } from './useSidebarData';
import { useNavAccess } from './provider';
import { useSettingsMenu } from './useSettingsMenu';

// The conversation list routes that show Mine / Unassigned / All.
const HOME_ROUTES = ['home', 'inbox_conversation'];
const ASSIGNEE_ICONS = { me: User16, unassigned: UserFollow16, all: Chat16 };

// Which area a page belongs to, read from the first segment after /app/accounts/:accountId/.
const AREA_OF_SEGMENT = {
  dashboard: 'inbox',
  conversations: 'inbox',
  mentions: 'inbox',
  participating: 'inbox',
  unattended: 'inbox',
  custom_view: 'inbox',
  team: 'inbox',
  label: 'inbox',
  inbox: 'inbox',
  'inbox-view': 'inbox',
  search: 'inbox',
  polaris: 'polaris',
  portals: 'knowledge',
  contacts: 'contacts',
  companies: 'contacts',
  reports: 'reports',
  campaigns: 'campaigns',
  settings: 'settings',
  profile: 'settings',
};

const labelDot = color =>
  h('span', {
    class: 'size-2 rounded-sm shrink-0',
    style: { backgroundColor: color },
  });

/**
 * The navigation of the two-tier shell: the areas of tier 1 and, for each, the menu of tier 2. Items keep the
 * old sidebar's routes, permissions, feature flags, counts and sorting; only where they live changed
 * (spec docs/superpowers/specs/2026-09-24-polaris-shell-design.md and DECISOES.md).
 */
export function useSidebarMenu() {
  const { t } = useI18n();
  const route = useRoute();
  const { accountScopedRoute } = useAccount();
  const { isAllowed, resolvePath } = useNavAccess();
  const { activeAssignee } = useAssigneeTab();
  const data = useSidebarData();
  const { settingsItems } = useSettingsMenu();

  const accountId = useMapGetter('getCurrentAccountId');
  const currentUser = useMapGetter('getCurrentUser');
  const conversationStats = useMapGetter('conversationStats/getStats');
  const appliedConversationFilters = useMapGetter(
    'getAppliedConversationFiltersV2'
  );
  const notificationsUnread = useMapGetter('notifications/getUnreadCount');

  const unread = count => (data.hasFilteredUnreadCounts.value ? count : 0);
  // Scoped lists (a channel, a team, a label, a folder, mentions...) always show all their conversations
  // (ChatList's isScopedList), so their links carry no ?assignee and leave the user's Mine / Unassigned / All
  // choice alone.
  const listRoute = (name, params = {}) => accountScopedRoute(name, params);

  const assigneeLabels = computed(() => ({
    me: t('CHAT_LIST.ASSIGNEE_TYPE_TABS.me'),
    unassigned: t('CHAT_LIST.ASSIGNEE_TYPE_TABS.unassigned'),
    all: t('CHAT_LIST.ASSIGNEE_TYPE_TABS.all'),
  }));

  // The store's Mine / Unassigned / All counts belong to whichever list was fetched last (a channel, a team, a
  // label, an advanced filter...). They only mean the whole inbox while that list is the unscoped one, so the
  // three items show them there and nowhere else: no number beats a wrong number.
  const showsAssigneeCounts = computed(
    () =>
      HOME_ROUTES.includes(route.name) &&
      !appliedConversationFilters.value?.length
  );

  const assigneeItems = computed(() =>
    filterItemsByPermission(
      ASSIGNEE_TYPE_TAB_PERMISSIONS,
      getUserPermissions(currentUser.value, accountId.value),
      item => item.permissions
    ).map(({ key, count }) => ({
      name: `assignee-${key}`,
      label: assigneeLabels.value[key],
      icon: ASSIGNEE_ICONS[key],
      to: accountScopedRoute('home', {}, { assignee: key }),
      badgeCount: showsAssigneeCounts.value
        ? conversationStats.value?.[count] || 0
        : 0,
      isActive: () =>
        HOME_ROUTES.includes(route.name) && activeAssignee.value === key,
    }))
  );

  const inboxItems = computed(() => [
    ...assigneeItems.value,
    {
      name: 'mentions',
      label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
      icon: At16,
      badgeCount: unread(data.mentionsUnreadCount.value),
      activeOn: ['conversation_mentions', 'conversation_through_mentions'],
      to: listRoute('conversation_mentions'),
    },
    {
      name: 'participating',
      label: t('SIDEBAR.PARTICIPATING_CONVERSATIONS'),
      icon: UserMultiple16,
      badgeCount: unread(data.participatingUnreadCount.value),
      activeOn: [
        'conversation_participating',
        'conversation_through_participating',
      ],
      to: listRoute('conversation_participating'),
    },
    {
      name: 'unattended',
      label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
      icon: Time16,
      badgeCount: unread(data.unattendedUnreadCount.value),
      activeOn: ['conversation_unattended', 'conversation_through_unattended'],
      to: listRoute('conversation_unattended'),
    },
    { name: 'sep-groups', separator: true },
    {
      name: 'folders',
      label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
      icon: Folder16,
      sort: data.buildSortConfig(SIDEBAR_SORT_SECTIONS.FOLDERS),
      children: data.sortedFolders.value.map(view => ({
        name: `folder-${view.id}`,
        label: view.name,
        // Folders shared with a team or the account carry a marker, as in the old sidebar.
        icon:
          view.visibility && view.visibility !== 'personal'
            ? UserMultiple16
            : undefined,
        badgeCount: unread(data.getFolderUnreadCount.value(view.id)),
        to: listRoute('folder_conversations', { id: view.id }),
      })),
    },
    {
      name: 'teams',
      label: t('SIDEBAR.TEAMS'),
      icon: Group16,
      sort: data.buildSortConfig(SIDEBAR_SORT_SECTIONS.TEAMS),
      children: data.sortedTeams.value.map(team => ({
        name: `team-${team.id}`,
        label: team.name,
        badgeCount: data.getTeamUnreadCount.value(team.id),
        to: listRoute('team_conversations', { teamId: team.id }),
      })),
    },
    {
      name: 'channels',
      label: t('SIDEBAR.CHANNELS'),
      icon: Connect16,
      sort: data.buildSortConfig(SIDEBAR_SORT_SECTIONS.CHANNELS),
      children: data.sortedInboxes.value.map(inbox => ({
        name: `inbox-${inbox.id}`,
        label: inbox.name,
        // A component, not a vnode (see useSettingsMenu: a vnode built in this computed can leak into the shell's render).
        icon: () => h(ChannelIcon, { inbox, class: 'size-4' }),
        badgeCount: data.getInboxUnreadCount.value(inbox.id),
        warning: inbox.reauthorization_required ? t('SIDEBAR.REAUTHORIZE') : '',
        to: listRoute('inbox_dashboard', { inbox_id: inbox.id }),
      })),
    },
    {
      name: 'labels',
      label: t('SIDEBAR.LABELS'),
      icon: Tag16,
      sort: data.buildSortConfig(SIDEBAR_SORT_SECTIONS.LABELS),
      children: data.sortedLabels.value.map(label => ({
        name: `label-${label.id}`,
        label: label.title,
        icon: labelDot(label.color),
        badgeCount: data.getLabelUnreadCount.value(label.id),
        to: listRoute('label_conversations', { label: label.title }),
      })),
    },
    { name: 'sep-notifications', separator: true },
    {
      name: 'notifications',
      label: t('SIDEBAR.NOTIFICATIONS'),
      icon: Notification16,
      badgeCount: notificationsUnread.value || 0,
      activeOn: ['inbox_view', 'inbox_view_conversation'],
      to: accountScopedRoute('inbox_view'),
    },
  ]);

  const polarisPage = (
    name,
    label,
    icon,
    navigationPath,
    activeOn,
    extra = {}
  ) => ({
    name,
    label,
    icon,
    activeOn,
    to: accountScopedRoute('polaris_assistants_index', { navigationPath }),
    ...extra,
  });

  // Menu em dois níveis do Polaris AI (spec 2026-10-08 §4.0): Começar · Treinar ▸ · Testar · Publicar ▸ · Analisar ▸
  // · Configurações. Os pontos de status (âmbar a fazer, azul atual, ✓ feito) vêm do setup_status do assistente da
  // rota e só aparecem enquanto o setup não está completo. O submenu de Voz é um marcador "Opcional" (Fase 5).
  const polarisSetupStatus = useMapGetter('polarisSetupStatus/getStatus');

  const polarisSetupDot = key => {
    const status = polarisSetupStatus.value(route.params.assistantId);
    if (!status || status.completed) return null;
    const step = status.steps?.find(item => item.key === key);
    if (!step) return null;
    return {
      tone: step.state,
      label: t(`SIDEBAR.POLARIS_SETUP_STATE_${step.state.toUpperCase()}`),
    };
  };

  const polarisItems = computed(() => [
    polarisPage(
      'get-started',
      t('SIDEBAR.POLARIS_GET_STARTED'),
      Flag16,
      'polaris_assistants_get_started_index',
      [
        'polaris_assistants_get_started_index',
        'polaris_assistants_create_index',
      ]
    ),
    { name: 'sep-polaris-train', separator: true },
    {
      name: 'train',
      label: t('SIDEBAR.POLARIS_TRAIN'),
      icon: Book16,
      children: [
        polarisPage(
          'train-playbook',
          t('SIDEBAR.POLARIS_TRAIN_PLAYBOOK'),
          undefined,
          'polaris_assistants_scenarios_index',
          ['polaris_assistants_scenarios_index'],
          { statusDot: polarisSetupDot('playbook') }
        ),
        polarisPage(
          'train-knowledge',
          t('SIDEBAR.POLARIS_TRAIN_KNOWLEDGE'),
          undefined,
          'polaris_assistants_documents_index',
          [
            'polaris_assistants_documents_index',
            'polaris_assistants_responses_index',
            'polaris_assistants_responses_pending',
          ],
          { statusDot: polarisSetupDot('knowledge') }
        ),
        polarisPage(
          'train-guidance',
          t('SIDEBAR.POLARIS_TRAIN_GUIDANCE'),
          undefined,
          'polaris_assistants_guidelines_index',
          [
            'polaris_assistants_guidelines_index',
            'polaris_assistants_guardrails_index',
          ],
          { statusDot: polarisSetupDot('guidance') }
        ),
        polarisPage(
          'train-integrations',
          t('SIDEBAR.POLARIS_TRAIN_INTEGRATIONS'),
          undefined,
          'polaris_tools_index',
          ['polaris_tools_index'],
          { statusDot: polarisSetupDot('integrations') }
        ),
      ],
    },
    polarisPage(
      'test',
      t('SIDEBAR.POLARIS_TEST'),
      Play16,
      'polaris_assistants_playground_index',
      ['polaris_assistants_playground_index'],
      { statusDot: polarisSetupDot('test') }
    ),
    {
      name: 'deploy',
      label: t('SIDEBAR.POLARIS_DEPLOY'),
      icon: Rocket16,
      children: [
        polarisPage(
          'deploy-messaging',
          t('SIDEBAR.POLARIS_DEPLOY_MESSAGING'),
          undefined,
          'polaris_assistants_inboxes_index',
          ['polaris_assistants_inboxes_index'],
          { statusDot: polarisSetupDot('deploy') }
        ),
        polarisPage(
          'deploy-voice',
          t('SIDEBAR.POLARIS_DEPLOY_VOICE'),
          undefined,
          'polaris_assistants_voice_deploy_index',
          ['polaris_assistants_voice_deploy_index'],
          { note: t('SIDEBAR.POLARIS_OPTIONAL') }
        ),
      ],
    },
    {
      name: 'analyze',
      label: t('SIDEBAR.POLARIS_ANALYZE'),
      icon: ChartLine16,
      children: [
        polarisPage(
          'analyze-performance',
          t('SIDEBAR.POLARIS_ANALYZE_PERFORMANCE'),
          undefined,
          'polaris_assistants_overview_index',
          ['polaris_assistants_overview_index']
        ),
      ],
    },
    { name: 'sep-polaris-settings', separator: true },
    polarisPage(
      'settings',
      t('SIDEBAR.POLARIS_SETTINGS'),
      SettingsAdjust16,
      'polaris_assistants_settings_index',
      ['polaris_assistants_settings_index', 'polaris_assistants_voice_index']
    ),
  ]);

  const portalPage = (name, label, icon, navigationPath, activeOn) => ({
    name,
    label,
    icon,
    activeOn,
    to: accountScopedRoute('portals_index', { navigationPath }),
  });

  const knowledgeItems = computed(() => [
    portalPage(
      'articles',
      t('SIDEBAR.HELP_CENTER.ARTICLES'),
      Document16,
      'portals_articles_index',
      [
        'portals_articles_index',
        'portals_articles_new',
        'portals_articles_edit',
      ]
    ),
    portalPage(
      'categories',
      t('SIDEBAR.HELP_CENTER.CATEGORIES'),
      Category16,
      'portals_categories_index',
      [
        'portals_categories_index',
        'portals_categories_articles_index',
        'portals_categories_articles_new',
        'portals_categories_articles_edit',
      ]
    ),
    portalPage(
      'locales',
      t('SIDEBAR.HELP_CENTER.LOCALES'),
      Translate16,
      'portals_locales_index',
      ['portals_locales_index']
    ),
    portalPage(
      'portal-settings',
      t('SIDEBAR.HELP_CENTER.SETTINGS'),
      SettingsAdjust16,
      'portals_settings_index',
      ['portals_settings_index']
    ),
  ]);

  const contactsItems = computed(() => [
    {
      name: 'all-contacts',
      label: t('SIDEBAR.ALL_CONTACTS'),
      icon: User16,
      to: accountScopedRoute(
        'contacts_dashboard_index',
        {},
        { page: 1, search: undefined }
      ),
      activeOn: ['contacts_dashboard_index', 'contacts_edit'],
    },
    {
      name: 'active-contacts',
      label: t('SIDEBAR.ACTIVE'),
      icon: UserAvatar16,
      to: accountScopedRoute('contacts_dashboard_active'),
      activeOn: ['contacts_dashboard_active', 'contacts_edit_active'],
    },
    {
      name: 'segments',
      label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
      icon: Group16,
      children: data.contactCustomViews.value.map(view => ({
        name: `segment-${view.id}`,
        label: view.name,
        to: accountScopedRoute(
          'contacts_dashboard_segments_index',
          { segmentId: view.id },
          { page: 1 }
        ),
        // A contact opened from this segment (/contacts/:id/segments/:segmentId).
        isActive: () =>
          route.name === 'contacts_edit_segment' &&
          String(route.params.segmentId) === String(view.id),
      })),
    },
    {
      name: 'tagged',
      label: t('SIDEBAR.TAGGED_WITH'),
      icon: Tag16,
      children: data.labels.value.map(label => ({
        name: `tagged-${label.id}`,
        label: label.title,
        icon: labelDot(label.color),
        to: accountScopedRoute(
          'contacts_dashboard_labels_index',
          { label: label.title },
          { page: 1, search: undefined }
        ),
        // A contact opened from this tag (/contacts/:id/labels/:label).
        isActive: () =>
          route.name === 'contacts_edit_label' &&
          route.params.label === label.title,
      })),
    },
    { name: 'sep-companies', separator: true },
    {
      name: 'companies',
      label: t('SIDEBAR.COMPANIES'),
      icon: Building16,
      to: accountScopedRoute(
        'companies_dashboard_index',
        {},
        { page: 1, search: undefined }
      ),
      activeOn: ['companies_dashboard_index', 'companies_dashboard_show'],
    },
  ]);

  const reportsItems = computed(() => [
    {
      name: 'report-overview',
      label: t('SIDEBAR.REPORTS_OVERVIEW'),
      icon: Dashboard16,
      to: accountScopedRoute('account_overview_reports'),
    },
    {
      name: 'report-conversation',
      label: t('SIDEBAR.REPORTS_CONVERSATION'),
      icon: Chat16,
      to: accountScopedRoute('conversation_reports'),
    },
    {
      name: 'report-agent',
      label: t('SIDEBAR.REPORTS_AGENT'),
      icon: User16,
      to: accountScopedRoute('agent_reports_index'),
      activeOn: ['agent_reports_show'],
    },
    {
      name: 'report-label',
      label: t('SIDEBAR.REPORTS_LABEL'),
      icon: Tag16,
      to: accountScopedRoute('label_reports_index'),
      activeOn: ['label_reports_show'],
    },
    {
      name: 'report-inbox',
      label: t('SIDEBAR.REPORTS_INBOX'),
      icon: Connect16,
      to: accountScopedRoute('inbox_reports_index'),
      activeOn: ['inbox_reports_show'],
    },
    {
      name: 'report-team',
      label: t('SIDEBAR.REPORTS_TEAM'),
      icon: Group16,
      to: accountScopedRoute('team_reports_index'),
      activeOn: ['team_reports_show'],
    },
    {
      name: 'report-csat',
      label: t('SIDEBAR.CSAT'),
      icon: ChartBar16,
      to: accountScopedRoute('csat_reports'),
    },
    {
      name: 'report-sla',
      label: t('SIDEBAR.REPORTS_SLA'),
      icon: Timer16,
      to: accountScopedRoute('sla_reports'),
    },
    {
      name: 'report-bot',
      label: t('SIDEBAR.REPORTS_BOT'),
      icon: Bot16,
      to: accountScopedRoute('bot_reports'),
    },
  ]);

  const campaignsItems = computed(() => [
    {
      name: 'campaign-livechat',
      label: t('SIDEBAR.LIVE_CHAT'),
      icon: Chat16,
      to: accountScopedRoute('campaigns_livechat_index'),
    },
    {
      name: 'campaign-sms',
      label: t('SIDEBAR.SMS'),
      icon: Phone16,
      to: accountScopedRoute('campaigns_sms_index'),
    },
    {
      name: 'campaign-whatsapp',
      label: t('SIDEBAR.WHATSAPP'),
      icon: ChatBot16,
      to: accountScopedRoute('campaigns_whatsapp_index'),
    },
  ]);

  // Only what the user may open: a group without children and an area without items disappear.
  const allowed = items =>
    items
      .map(item =>
        item.children
          ? { ...item, children: item.children.filter(c => isAllowed(c.to)) }
          : item
      )
      .filter(item => {
        if (item.separator) return true;
        if (item.children) return item.children.length > 0;
        return isAllowed(item.to);
      })
      // no separator at the edges or next to another one
      .filter((item, i, list) => {
        if (!item.separator) return true;
        const prev = list[i - 1];
        const next = list[i + 1];
        return prev && next && !prev.separator && !next.separator;
      });

  const firstLink = items => {
    const flat = items.flatMap(item => item.children || [item]);
    return flat.find(item => item.to)?.to;
  };

  const area = (name, label, icon, items, placement = 'main') => {
    const visible = allowed(items);
    return {
      name,
      label,
      icon,
      placement,
      items: visible,
      to: firstLink(visible),
    };
  };

  const areas = computed(() =>
    [
      area('inbox', t('SIDEBAR.AREA_INBOX'), Chat20, inboxItems.value),
      area('polaris', t('SIDEBAR.POLARIS'), Bot20, polarisItems.value),
      area('knowledge', t('SIDEBAR.KNOWLEDGE'), Book20, knowledgeItems.value),
      area('contacts', t('SIDEBAR.CONTACTS'), User20, contactsItems.value),
      area('reports', t('SIDEBAR.REPORTS'), ChartLine20, reportsItems.value),
      area('campaigns', t('SIDEBAR.CAMPAIGNS'), Email20, campaignsItems.value),
      area(
        'settings',
        t('SIDEBAR.SETTINGS'),
        Settings16,
        settingsItems.value,
        'footer'
      ),
    ].filter(item => item.items.length > 0)
  );

  const activeArea = computed(() => {
    const name = AREA_OF_SEGMENT[route.path.split('/')[4]];
    return areas.value.find(item => item.name === name) ?? null;
  });

  // The current item of tier 2: an item that says so (Mine / Unassigned / All), then one that lists the page in
  // activeOn, then the exact path, then the longest path the page sits under (not an `exact` item: Settings' Home
  // sits over every settings page and would light up for a page with no item of its own).
  const activeItemName = computed(() => {
    const flat = (activeArea.value?.items ?? [])
      .flatMap(item => item.children || [item])
      .filter(item => item.to);
    const explicit = flat.find(item => item.isActive?.());
    if (explicit) return explicit.name;
    const byName = flat.find(item => item.activeOn?.includes(route.name));
    if (byName) return byName.name;
    const byPath = flat.find(item => resolvePath(item.to) === route.path);
    if (byPath) return byPath.name;
    const under = flat
      .filter(
        item => !item.exact && route.path.startsWith(`${resolvePath(item.to)}/`)
      )
      .sort((a, b) => resolvePath(b.to).length - resolvePath(a.to).length);
    return under[0]?.name ?? null;
  });

  const isItemActive = item => activeItemName.value === item.name;

  return { areas, activeArea, isItemActive };
}
